import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, 
         AlignmentType, HeadingLevel, BorderStyle, WidthType, ShadingType } from 'docx'
import { saveAs } from 'file-saver'

/**
 * Exporta el proyecto BREEAM a formato Word con el estilo OFICIAL BREEAM ES NC 2015
 * Formato exacto: muestra texto completo de cada criterio seguido de justificación
 */
export async function exportToWord(project, BREEAM_CRITERIA) {
  const totalPoints = project.totalPoints || 0
  const maxPoints = project.maxPoints || 110
  // El informe Word oficial BREEAM ES usa la puntuación PONDERADA por
  // categorías (Tabla 5 del manual NC 2015), no la bruta. computeProjectStats
  // (en App.js) ya calcula project.breeamPct con la ponderación correcta.
  const percentage = project.breeamPct != null
    ? Math.round(project.breeamPct)
    : Math.round((totalPoints / maxPoints) * 100)

  // Bordes para tablas
  const border = { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" }
  const borders = { top: border, bottom: border, left: border, right: border }

  // Agrupar evaluaciones por categoría
  const evaluationsByCategory = {}
  Object.entries(project.evaluations || {}).forEach(([key, evaluation]) => {
    if (!evaluationsByCategory[evaluation.category]) {
      evaluationsByCategory[evaluation.category] = []
    }
    evaluationsByCategory[evaluation.category].push(evaluation)
  })

  const sections = []

  // ═══════════════════════════════════════════════════════════
  // 1. RESUMEN EJECUTIVO
  // ═══════════════════════════════════════════════════════════
  
  sections.push(
    new Paragraph({ 
      text: "Resumen Ejecutivo",
      heading: HeadingLevel.HEADING_1
    }),
    new Paragraph({
      children: [new TextRun({ 
        text: `BREEAM Analyzer ha realizado la evaluación BREEAM ES de ${project.name}. Este informe detalla las prestaciones del edificio según la evaluación realizada.`,
        font: "Frutiger LT 45 Light",
        bold: true,
        size: 18
      })],
      spacing: { after: 120 }
    }),
    new Paragraph({
      children: [new TextRun({
        text: `El edificio ha conseguido una puntuación BREEAM ES de ${percentage}%, lo que se traduce en una clasificación en la fase post-construcción de ${getClassification(percentage)}.`,
        font: "Frutiger LT 45 Light",
        size: 18
      })],
      spacing: { after: 360 }
    })
  )

  // ═══════════════════════════════════════════════════════════
  // 2. DATOS DEL EDIFICIO
  // ═══════════════════════════════════════════════════════════
  
  sections.push(
    new Paragraph({
      text: "Datos del Equipo de Proyecto y del Edificio",
      heading: HeadingLevel.HEADING_1
    }),
    new Paragraph({
      children: [new TextRun({ text: "Datos del Edificio", font: "Arial", bold: true, size: 20, color: "3D6864" })],
      spacing: { before: 120, after: 120 }
    })
  )

  // ═══════════════════════════════════════════════════════════
  // 3. RESUMEN DE PRESTACIONES
  // ═══════════════════════════════════════════════════════════
  
  sections.push(
    new Paragraph({
      text: "Resumen de las Prestaciones del Edificio según la Evaluación",
      heading: HeadingLevel.HEADING_1
    }),
    new Paragraph({
      children: [new TextRun({
        text: `${project.name} consigue una puntuación final de ${percentage}% según el método de evaluación y certificación de la sostenibilidad BREEAM ES NUEVA CONSTRUCCIÓN 2015. Esto se traduce en una clasificación de ${getClassification(percentage)}.`,
        font: "Frutiger LT 45 Light",
        size: 18
      })],
      spacing: { after: 240 }
    })
  )

  // Tabla de resumen por categorías
  const categoryRows = [
    new TableRow({
      tableHeader: true,
      children: [
        new TableCell({
          borders,
          width: { size: 4500, type: WidthType.DXA },
          shading: { fill: "3D6764", type: ShadingType.CLEAR },
          margins: { top: 100, bottom: 100, left: 120, right: 120 },
          children: [new Paragraph({ 
            children: [new TextRun({ text: "Categoría", bold: true, color: "FFFFFF", size: 18 })],
            alignment: AlignmentType.LEFT
          })]
        }),
        new TableCell({
          borders,
          width: { size: 1600, type: WidthType.DXA },
          shading: { fill: "3D6764", type: ShadingType.CLEAR },
          margins: { top: 100, bottom: 100, left: 120, right: 120 },
          children: [new Paragraph({ 
            children: [new TextRun({ text: "Puntos", bold: true, color: "FFFFFF", size: 18 })],
            alignment: AlignmentType.CENTER
          })]
        }),
        new TableCell({
          borders,
          width: { size: 1600, type: WidthType.DXA },
          shading: { fill: "3D6764", type: ShadingType.CLEAR },
          margins: { top: 100, bottom: 100, left: 120, right: 120 },
          children: [new Paragraph({ 
            children: [new TextRun({ text: "% obtenido", bold: true, color: "FFFFFF", size: 18 })],
            alignment: AlignmentType.CENTER
          })]
        }),
        new TableCell({
          borders,
          width: { size: 1660, type: WidthType.DXA },
          shading: { fill: "3D6764", type: ShadingType.CLEAR },
          margins: { top: 100, bottom: 100, left: 120, right: 120 },
          children: [new Paragraph({ 
            children: [new TextRun({ text: "Clasificación", bold: true, color: "FFFFFF", size: 18 })],
            alignment: AlignmentType.CENTER
          })]
        })
      ]
    })
  ]

  Object.entries(BREEAM_CRITERIA).forEach(([catName, catData]) => {
    const catEvals = evaluationsByCategory[catName] || []
    const catPoints = catEvals.reduce((sum, e) => sum + (e.puntos_auditoria ?? e.points_obtained ?? 0), 0)
    const catPct = Math.round((catPoints / catData.maxPoints) * 100)

    categoryRows.push(
      new TableRow({
        children: [
          new TableCell({
            borders,
            width: { size: 4500, type: WidthType.DXA },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ children: [new TextRun({ text: catName, size: 18 })] })]
          }),
          new TableCell({
            borders,
            width: { size: 1600, type: WidthType.DXA },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ 
              children: [new TextRun({ text: `${catPoints}/${catData.maxPoints}`, size: 18 })],
              alignment: AlignmentType.CENTER
            })]
          }),
          new TableCell({
            borders,
            width: { size: 1600, type: WidthType.DXA },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ 
              children: [new TextRun({ text: `${catPct}%`, size: 18 })],
              alignment: AlignmentType.CENTER
            })]
          }),
          new TableCell({
            borders,
            width: { size: 1660, type: WidthType.DXA },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ 
              children: [new TextRun({ text: getCategoryClass(catPct), size: 18 })],
              alignment: AlignmentType.CENTER
            })]
          })
        ]
      })
    )
  })

  sections.push(
    new Table({
      width: { size: 9360, type: WidthType.DXA },
      columnWidths: [4500, 1600, 1600, 1660],
      rows: categoryRows
    }),
    new Paragraph({ text: "", spacing: { after: 480 } })
  )

  // ═══════════════════════════════════════════════════════════
  // 4. EVALUACIÓN DETALLADA
  // ═══════════════════════════════════════════════════════════
  
  sections.push(
    new Paragraph({
      text: "Evaluación detallada del comportamiento medioambiental del edificio",
      heading: HeadingLevel.HEADING_1
    }),
    new Paragraph({
      children: [new TextRun({
        text: "La siguiente sección contiene cada uno de los Requisitos de la evaluación BREEAM ES, y la información que se adjunta como evidencia del comportamiento de los edificios para demostrar el cumplimiento de dichos requisitos.",
        font: "Frutiger LT 45 Light",
        size: 18
      })],
      spacing: { after: 120 }
    }),
    new Paragraph({
      children: [new TextRun({
        text: "Cada Requisito recoge la justificación realizada por el Asesor que resume la evaluación del comportamiento medioambiental del edificio, validando el número de puntos obtenidos.",
        font: "Frutiger LT 45 Light",
        size: 18
      })],
      spacing: { after: 360 }
    })
  )

  // Por cada categoría con evaluaciones
  Object.entries(BREEAM_CRITERIA).forEach(([catName, catData]) => {
    const catEvals = evaluationsByCategory[catName] || []
    if (catEvals.length === 0) return

    // Nombre de la categoría
    sections.push(
      new Paragraph({ text: "", pageBreakBefore: true }),
      new Paragraph({
        text: catName.toUpperCase(),
        heading: HeadingLevel.HEADING_1
      })
    )

    // Por cada criterio evaluado
    catEvals.forEach(evaluation => {
      const critData = catData.criteria[evaluation.criteria_code]
      if (!critData) return

      let resultado = null
      try {
        resultado = typeof evaluation.justificacion === 'string' 
          ? JSON.parse(evaluation.justificacion) 
          : evaluation.justificacion
      } catch {}

      // ─── TÍTULO DEL CRITERIO (Heading 2) ───
      sections.push(
        new Paragraph({
          text: `${evaluation.criteria_code} - ${evaluation.criteria_name}`,
          heading: HeadingLevel.HEADING_2
        })
      )

      // ─── OBJETIVO ───
      if (critData.objetivo) {
        sections.push(
          new Paragraph({
            children: [new TextRun({ text: "Objetivo", bold: true, size: 24, color: "6CB43F" })],
            spacing: { before: 120, after: 80 }
          }),
          new Paragraph({
            children: [new TextRun({ 
              text: critData.objetivo,
              font: "Frutiger LT 45 Light",
              size: 18
            })],
            spacing: { after: 180 }
          })
        )
      }

      // ─── DOCUMENTACIÓN PARA ENTREGAR ───
      sections.push(
        new Paragraph({
          children: [new TextRun({ text: "Documentación para entregar", bold: true, size: 24, color: "6CB43F" })],
          spacing: { before: 120, after: 120 }
        })
      )

      // ─── JUSTIFICACIÓN ───
      sections.push(
        new Paragraph({
          children: [new TextRun({ text: "Justificación", bold: true, size: 24, color: "6CB43F" })],
          spacing: { before: 120, after: 120 }
        })
      )

      // Puntuación obtenida (estilo Default, Montserrat 8pt)
      const labelPuntos = (n) =>
        n === 1 ? "Un punto" :
        n === 2 ? "Dos puntos" :
        n === 3 ? "Tres puntos" :
        n === 4 ? "Cuatro puntos" :
        n === 5 ? "Cinco puntos" :
        n === 6 ? "Seis puntos" :
        `${n} puntos`
      const techPts = evaluation.points_obtained || 0
      const hasAudit = evaluation.puntos_auditoria != null
      const finalPts = hasAudit ? evaluation.puntos_auditoria : techPts

      if (hasAudit) {
        // Bloque diferenciado: técnica + auditoría final + motivo
        sections.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Puntuación técnica del asesor: ", font: "Montserrat", size: 16 }),
              new TextRun({ text: labelPuntos(techPts), font: "Montserrat", size: 16, bold: true }),
            ],
            spacing: { after: 60 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: "Auditoría final del certificador", font: "Montserrat", size: 16, bold: true, color: "C98A2E" }),
              ...(evaluation.fecha_auditoria
                ? [new TextRun({ text: ` (${new Date(evaluation.fecha_auditoria).toLocaleDateString('es-ES')})`, font: "Montserrat", size: 16, color: "7A6A5A" })]
                : []),
              new TextRun({ text: ": ", font: "Montserrat", size: 16, bold: true, color: "C98A2E" }),
              new TextRun({ text: labelPuntos(finalPts), font: "Montserrat", size: 16, bold: true, color: "C98A2E" }),
            ],
            spacing: { after: evaluation.motivo_auditoria ? 60 : 120 },
          }),
        )
        if (evaluation.motivo_auditoria) {
          sections.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Motivo: ", font: "Montserrat", size: 16, bold: true, color: "7A4A10" }),
                new TextRun({ text: evaluation.motivo_auditoria, font: "Montserrat", size: 16, italics: true, color: "5C4A3A" }),
              ],
              spacing: { after: 180 },
              shading: { type: ShadingType.CLEAR, color: "auto", fill: "FFF8E6" },
            }),
          )
        }
      } else {
        sections.push(
          new Paragraph({
            children: [new TextRun({ text: labelPuntos(finalPts), font: "Montserrat", size: 16 })],
            spacing: { after: 120 },
          }),
        )
      }

      // ─── CRITERIOS CON JUSTIFICACIÓN (FORMATO OFICIAL) ───
      if (resultado?.subcriteria && Array.isArray(resultado.subcriteria)) {
        resultado.subcriteria.forEach((sub, idx) => {
          const estado = sub.estado || (sub.cumple ? 'SÍ' : 'NO')
          const textoCompletoDelCriterio = critData.subcriteria[sub.numero - 1] || `Criterio ${sub.numero}`

          // ───── FORMATO OFICIAL: Criterio N – [TEXTO COMPLETO]... ¿Se cumple? SÍ/NO ─────
          // Este es el formato [Default] con Montserrat 9pt (18 en size Word)
          
          sections.push(
            new Paragraph({
              children: [
                new TextRun({ 
                  text: `Criterio ${sub.numero} – ${textoCompletoDelCriterio}`,
                  font: "Montserrat",
                  size: 18
                }),
                new TextRun({
                  text: ` ¿Se cumple? `,
                  font: "Montserrat",
                  bold: true,
                  size: 18
                }),
                new TextRun({
                  text: estado,
                  font: "Montserrat",
                  bold: true,
                  size: 18,
                  color: sub.cumple ? "000000" : "000000"  // Negro en ambos casos como el original
                })
              ],
              spacing: { before: 180, after: 120 }
            })
          )

          // ───── JUSTIFICACIÓN EN PÁRRAFOS [Normal] con Montserrat ─────
          // Dividir la justificación en párrafos si contiene saltos de línea
          const justificacionParrafos = (sub.justificacion || 'Sin justificación disponible.')
            .split('\n')
            .filter(p => p.trim())

          justificacionParrafos.forEach((parrafo, pIdx) => {
            sections.push(
              new Paragraph({
                children: [new TextRun({ 
                  text: parrafo.trim(),
                  font: "Montserrat",
                  size: 18
                })],
                spacing: { 
                  after: pIdx === justificacionParrafos.length - 1 && idx === resultado.subcriteria.length - 1 ? 240 : 120 
                }
              })
            )
          })
        })
      } else if (resultado && typeof resultado === 'object') {
        // Formato alternativo (ej. MAT 3): sin subcriteria pero con resumen ejecutivo, nivel de cumplimiento, riesgos
        if (resultado.nivel_cumplimiento_alcanzado) {
          sections.push(
            new Paragraph({
              children: [new TextRun({ text: "Nivel de cumplimiento alcanzado:", font: "Montserrat", bold: true, size: 18 })],
              spacing: { before: 180, after: 60 }
            }),
            new Paragraph({
              children: [new TextRun({ text: resultado.nivel_cumplimiento_alcanzado, font: "Montserrat", size: 18 })],
              spacing: { after: 120 }
            })
          )
        }

        if (resultado.resumen_ejecutivo) {
          sections.push(
            new Paragraph({
              children: [new TextRun({ text: "Conclusión del Asesor BREEAM ES:", font: "Montserrat", bold: true, size: 18 })],
              spacing: { before: 180, after: 80 }
            })
          )
          resultado.resumen_ejecutivo.split('\n').filter(l => l.trim()).forEach(line => {
            sections.push(
              new Paragraph({
                children: [new TextRun({ text: line.trim(), font: "Montserrat", size: 18 })],
                spacing: { after: 100 }
              })
            )
          })
        }

        if (Array.isArray(resultado.evidencias_verificadas) && resultado.evidencias_verificadas.length > 0) {
          sections.push(
            new Paragraph({
              children: [new TextRun({ text: "Evidencias verificadas:", font: "Montserrat", bold: true, size: 18 })],
              spacing: { before: 180, after: 80 }
            })
          )
          resultado.evidencias_verificadas.forEach(item => {
            const line = typeof item === 'string' ? item : (item.descripcion || item.nombre || JSON.stringify(item))
            sections.push(
              new Paragraph({
                children: [new TextRun({ text: `• ${line}`, font: "Montserrat", size: 18 })],
                spacing: { after: 60 },
                indent: { left: 360 }
              })
            )
          })
        }

        if (Array.isArray(resultado.riesgos_auditor) && resultado.riesgos_auditor.length > 0) {
          sections.push(
            new Paragraph({
              children: [new TextRun({ text: "Riesgos identificados por el asesor:", font: "Montserrat", bold: true, size: 18 })],
              spacing: { before: 180, after: 80 }
            })
          )
          resultado.riesgos_auditor.forEach(r => {
            const line = typeof r === 'string' ? r : (r.descripcion || JSON.stringify(r))
            sections.push(
              new Paragraph({
                children: [new TextRun({ text: `• ${line}`, font: "Montserrat", size: 18 })],
                spacing: { after: 60 },
                indent: { left: 360 }
              })
            )
          })
        }

        if (Array.isArray(resultado.evidencias_faltantes) && resultado.evidencias_faltantes.length > 0) {
          sections.push(
            new Paragraph({
              children: [new TextRun({ text: "Evidencias pendientes de aportar:", font: "Montserrat", bold: true, size: 18 })],
              spacing: { before: 180, after: 80 }
            })
          )
          resultado.evidencias_faltantes.forEach(item => {
            const line = typeof item === 'string' ? item : (item.descripcion || item.nombre || JSON.stringify(item))
            sections.push(
              new Paragraph({
                children: [new TextRun({ text: `• ${line}`, font: "Montserrat", size: 18 })],
                spacing: { after: 60 },
                indent: { left: 360 }
              })
            )
          })
        }
      } else {
        // Si no hay criterios detallados ni objeto estructurado, mostrar mensaje simple
        sections.push(
          new Paragraph({
            children: [new TextRun({
              text: `Este criterio obtuvo ${finalPts}/${critData.points} puntos.`,
              font: "Montserrat",
              size: 18
            })],
            spacing: { after: 240 }
          })
        )
      }

      // Documentación analizada (opcional, al final)
      if (resultado?.documentacion_analizada && resultado.documentacion_analizada.length > 0) {
        sections.push(
          new Paragraph({
            children: [new TextRun({ 
              text: "Documentación analizada:", 
              font: "Montserrat",
              bold: true, 
              size: 18 
            })],
            spacing: { before: 180, after: 100 }
          })
        )
        
        resultado.documentacion_analizada.forEach(doc => {
          sections.push(
            new Paragraph({
              children: [new TextRun({ text: `• ${doc}`, font: "Montserrat", size: 18 })],
              spacing: { after: 60 },
              indent: { left: 360 }
            })
          )
        })
      }
    })
  })

  // ═══════════════════════════════════════════════════════════
  // CREAR DOCUMENTO CON ESTILOS OFICIALES BREEAM
  // ═══════════════════════════════════════════════════════════

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { 
            font: "Frutiger LT 45 Light",
            size: 18  // 9pt
          },
          paragraph: {
            spacing: { before: 120, after: 120, line: 276 }  // 1.15 interlineado
          }
        }
      },
      paragraphStyles: [
        {
          id: "Heading1",
          name: "Heading 1",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { 
            font: "Calvert MT",
            size: 28,  // 14pt
            bold: true,
            color: "3D6764"
          },
          paragraph: { 
            spacing: { before: 240, after: 120 },
            outlineLevel: 0
          }
        },
        {
          id: "Heading2",
          name: "Heading 2",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { 
            font: "Calvert MT",
            size: 32,  // 16pt
            bold: true,
            color: "3D6864"
          },
          paragraph: { 
            spacing: { before: 240, after: 60 },
            outlineLevel: 1
          }
        }
      ]
    },
    sections: [{
      properties: {
        page: {
          size: { width: 11906, height: 16838 },  // A4
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 }  // 1 pulgada
        }
      },
      children: sections
    }]
  })

  // Generar y descargar
  const blob = await Packer.toBlob(doc)
  const fileName = `Informe_BREEAM_${project.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`
  saveAs(blob, fileName)
}

// ═══════════════════════════════════════════════════════════
// FUNCIONES AUXILIARES
// ═══════════════════════════════════════════════════════════

function getClassification(percentage) {
  if (percentage >= 85) return 'Excepcional'
  if (percentage >= 70) return 'Excelente'
  if (percentage >= 55) return 'Muy Bueno'
  if (percentage >= 45) return 'Bueno'
  if (percentage >= 30) return 'Correcto'
  return 'Aprobado'
}

function getCategoryClass(percentage) {
  if (percentage >= 85) return 'Excepcional'
  if (percentage >= 70) return 'Excelente'
  if (percentage >= 55) return 'Muy Bueno'
  if (percentage >= 45) return 'Bueno'
  if (percentage >= 30) return 'Correcto'
  if (percentage > 0) return 'Aprobado'
  return 'No evaluado'
}
